import React from "react";
import { graphql } from "react-relay";

import { faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { RunQuery } from "~/lib/relayUtil";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import { Button, LoadingSpinner, Tooltip } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type { SummaryModalStreamInfoQuery } from "~/__generated__/SummaryModalStreamInfoQuery.graphql";

import mergeStyles from "~/notes/css/MergeStream.module.css";

import styles from "../css/AddStreamModal.module.css";

export type SummaryModalResult = { action: "cancel" } | { action: "add"; shareWithUsers: boolean };

interface SummaryModalProps {
	currentStreamId: string;
	selectedStreamId: string;
	onClose: (action: SummaryModalResult) => void;
}

export const getStreamInfo = graphql`
	query SummaryModalStreamInfoQuery($currentStreamId: ID!, $selectedStreamId: ID!) {
		viewer {
			account {
				allowExternalInviting
			}
		}

		currentStream: noteStream(id: $currentStreamId) {
			viewerNotestreamuser {
				id
			}

			notestreamusers {
				id
				accessType
				user {
					id
					fullName
					isExternal
				}
			}
		}

		selectedStream: noteStream(id: $selectedStreamId) {
			type
			displayName
			viewerNotestreamuser {
				accessType
			}

			notestreamusers {
				id
				accessType
				user {
					id
					fullName
				}
			}
		}
	}
`;

export const openSummaryModal = async (
	currentStreamId: string,
	selectedStreamId: string,
): Promise<SummaryModalResult | null | undefined> => {
	const res = await __globalModalContext?.open<SummaryModalResult>(({ close }) => (
		<SummaryModal currentStreamId={currentStreamId} selectedStreamId={selectedStreamId} onClose={close} />
	));

	return res;
};

export default function SummaryModal({ currentStreamId, selectedStreamId, onClose }: SummaryModalProps) {
	const [result, setResult] = React.useState<SummaryModalStreamInfoQuery["response"] | undefined>();
	const [loading, setLoading] = React.useState(false);
	const [shareCheckboxValue, setShareCheckboxValue] = React.useState(false);

	const onCancel = React.useCallback(() => onClose({ action: "cancel" }), [onClose]);
	const onAdd = React.useCallback(
		() => onClose({ action: "add", shareWithUsers: shareCheckboxValue }),
		[onClose, shareCheckboxValue],
	);
	const onQueryLoading = React.useCallback(() => {
		setLoading(true);
	}, []);

	const onQueryLoaded = React.useCallback((response: SummaryModalStreamInfoQuery["response"]) => {
		setResult(response);
		setLoading(false);
	}, []);

	return (
		<Modal onClose={onCancel}>
			<Modal.Header noBottomBorder className={mergeStyles.summaryModalHeader} onClose={onCancel}>
				<Modal.Title>Add Fellow notes as a tab</Modal.Title>
			</Modal.Header>

			<Modal.Body className={styles.modalBody}>
				<RunQuery<SummaryModalStreamInfoQuery>
					query={getStreamInfo}
					onResult={onQueryLoaded}
					variables={{ currentStreamId, selectedStreamId }}
					onLoading={onQueryLoading}
				/>
				{loading && <LoadingSpinner dimmer={false} />}
				{result && (
					<SummaryModalInner
						response={result}
						shareCheckboxValue={shareCheckboxValue}
						setShareCheckboxValue={setShareCheckboxValue}
					/>
				)}
			</Modal.Body>

			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={onAdd}>
					Add
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

type SummaryModalInnerProps = {
	response: SummaryModalStreamInfoQuery["response"];
	shareCheckboxValue: boolean;
	setShareCheckboxValue: (val: boolean) => void;
};

function SummaryModalInner({ response, shareCheckboxValue, setShareCheckboxValue }: SummaryModalInnerProps) {
	const changeShareValue = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setShareCheckboxValue(event.target.checked);
		},
		[setShareCheckboxValue],
	);

	if (!response.currentStream || !response.selectedStream) {
		return null;
	}

	const selectedUserIds = response.selectedStream.notestreamusers.map(nsu => nsu.user.id);
	const usersToShare = response.currentStream.notestreamusers
		.filter(
			nsu =>
				!selectedUserIds.includes(nsu.user.id) &&
				(response.viewer.account.allowExternalInviting || !nsu.user.isExternal),
		)
		.map(nsu => nsu.user);
	const sharingWithExternalUsers =
		response.viewer.account.allowExternalInviting && usersToShare.some(u => u.isExternal);

	const firstUsers = usersToShare.slice(0, 2).map(u => u.fullName);
	const firstUsersText = firstUsers.join(", ");
	const otherLength = usersToShare.length - 2;
	const isPartial = response.selectedStream.viewerNotestreamuser?.accessType === "partial";
	const isOneOnOne = response.selectedStream.type === "oneonone";
	const streamUserNames = response.currentStream.notestreamusers.map(nsu =>
		nsu.id === response.currentStream?.viewerNotestreamuser?.id ? `${nsu.user.fullName} (you)` : nsu.user.fullName,
	);

	return (
		<>
			<div className={styles.infoMessage}>
				Notes from <b>{response.selectedStream.displayName}</b> will be added as a tab. This tab will be visible to
				everyone, but the contents of the notes will only be visible to users who previously had access.
			</div>

			{usersToShare.length > 0 && (
				<div className={styles.shareBlock}>
					<div className={styles.shareBlock__header}>
						<FontAwesomeIcon icon={faUser} />
						User access
					</div>

					{isOneOnOne ? (
						<div className={styles.shareBlock__info}>
							This is a 1-on-1 series between <b>{streamUserNames[0]}</b> and <b>{streamUserNames[1]}</b>. Notes will
							remain visible to only these two people.
						</div>
					) : (
						<>
							<div className={styles.shareBlock__info}>
								By default, users will only be able to access to the notes they previously had access to. Share the
								series to give access to all notes.
							</div>

							{sharingWithExternalUsers && (
								<div className={styles.shareBlock__info}>
									<ConnectedIcon style={{ marginRight: "0.25rem" }} /> Some users are from other workspaces. By sharing
									this series, you will give them access to all notes in {response.selectedStream.displayName}.
								</div>
							)}

							<Tooltip
								placement="top"
								disabled={!isPartial}
								content={
									<div className={styles.noAccessTooltip}>
										You have partial access to this series and cannot perform this action
									</div>
								}
							>
								<div className={styles.checkboxWrapper}>
									<Checkbox
										onChange={changeShareValue}
										checked={shareCheckboxValue}
										label="Share?"
										id="shareCheckbox"
										className={styles.checkbox}
										disabled={isPartial}
									/>
									<label htmlFor="shareCheckbox" className={cx({ [styles.disabled]: isPartial })}>
										Share series with {firstUsersText}
										{otherLength > 0 && (
											<span>
												{" "}
												and {otherLength} other{otherLength > 1 ? "s" : ""}
											</span>
										)}
									</label>
								</div>
							</Tooltip>
						</>
					)}
				</div>
			)}
		</>
	);
}
